import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useWindowScroll } from 'react-use';
import { fetchArticle, touchUserArticle } from '../API/ArticleApi';
import { useAuth } from '../Auth/AuthProvider';
import CollaborationIntroModal from '../Builder/BookBuilder/CollaborationIntroModal';
import CoAuthorAbandoned from '../Components/CoAuthorAbandoned';
import CoAuthorTag from '../Components/CoAuthorTag';
import CollaborationStatusTag from '../Components/CollaborationStatusTag';
import LoadingIcon from '../Components/LoadingIcon';
import SignUpPrompt from '../Components/SignUpPrompt';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import { useNotifications } from '../Context/NotificationProvider';
import { useSession } from '../Context/ReadingSessionProvider';
import { useBookChapterContext } from '../ReadBook/Context/BookChapterContextProvider';
import { ArticleOpenedEvent } from '../Session/SessionEvents/ArticleOpenedEvent';
import { ArticleProgressChangeEvent } from '../Session/SessionEvents/ArticleProgressChangeEvent';
import { Article, ArticleStatus } from '../Types/Article';
import { Question, QuestionType } from '../Types/Question';
import { UserType } from '../Types/User';
import {
  UserArticleProgressState,
  UserArticleQuizStage,
} from '../Types/UserArticle';
import {
  Feature,
  getFeatureIntroducedToUserAt,
  recordFeatureIntroducedToUserAt,
} from '../Utils/FeatureUsage';
import { countWords, splitIntoLines, splitIntoWords } from '../Utils/Text';
import ArticleContent from './ArticleViewComponents/ArticleContent';
import ArticleCover from './ArticleViewComponents/ArticleCover';
import ArticleStats from './ArticleViewComponents/ArticleStats';
import ReadArticleSidebar, {
  ReadArticleSidebarContentType,
} from './ReadArticleSidebar';
import { Paragraph, ReadArticleViewProps, Section } from './Types/ArticleTypes';
import { useArticleActivity } from './hooks/useArticleActivity';
import { useArticleReadAloud } from './hooks/useArticleReadAloud';

const ARTICLE_READ_SCROLL_BUFFER = 100;

function ReadArticleView(props: ReadArticleViewProps) {
  const { articleId = props.articleId } = useParams<{
    articleId: string;
  }>();

  const { userType, user } = useAuth();
  const { book, chapter } = useBookChapterContext();
  const { setDoNotDisturb } = useNotifications();
  const { recordEvent, initSession } = useSession();
  const { trackEvent } = useAnalytics();
  const { y: windowScrollY } = useWindowScroll();
  const location = useLocation();

  const [article, setArticle] = useState<Article | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [isArticleLoaded, setIsArticleLoaded] = useState<boolean | null>(false);
  const [error, setError] = useState<Error | null>(null);
  const [isCollaborationIntroOpen, setCollaborationIntroOpen] = useState(false);

  const myCoAuthor = user && book?.getMyCoAuthor(user.id);

  const paragraphs = useMemo<Paragraph[]>(() => {
    if (!sections) return [];

    const result = [];
    for (const section of sections) {
      const lines = splitIntoLines(section.text);
      result.push(
        ...lines.map(({ line, lineStartsAt }) => ({
          section: section,
          text: line,
          words: splitIntoWords(line, true),
          startIndex: section.startIndex + lineStartsAt,
          endIndex: section.startIndex + lineStartsAt + line.length - 1,
        })),
      );
    }

    return result;
  }, [sections]);

  useEffect(() => {
    // Wait until user is loaded. If unauthenticated, it will be null
    if (user === undefined) return;

    const loadArticle = async () => {
      if (user) {
        await initSession();
        await recordEvent(new ArticleOpenedEvent(articleId));
      }

      try {
        setIsArticleLoaded(false);

        const [article] = await Promise.all([
          fetchArticle(articleId),
          user ? touchUserArticle(user.id, articleId) : Promise.resolve(),
        ]);

        if (!article || article.status !== ArticleStatus.ACTIVE) {
          setError(new Error('Story not found'));
          setIsArticleLoaded(null);
          return;
        }

        trackEvent(AnalyticsEvent.STORY_OPENED, {
          articleId: articleId,
          article: article.title,
          createdBy: article.createdByUserId,
          isOwnArticle: user && article.createdByUserId === user.id,
        });

        // Use a single section for the whole article by default, no blurring
        setSections([
          {
            text: article.passage,
            questionId: null,
            startIndex: 0,
            endIndex: article.passage.length - 1,
            isBlurred: false,
          },
        ]);
        setArticle(article);
        setIsArticleLoaded(true);
      } catch (error) {
        setError(error);
        setIsArticleLoaded(null);
      }
    };

    loadArticle();
  }, [user, articleId]);

  useEffect(() => {
    if (!article) return;
    if (
      (article?.coverImage || article?.coverVideo) &&
      article?.status === ArticleStatus.ACTIVE &&
      article.challengingWordsGeneratedAt &&
      article.quizGeneratedAt &&
      (!article.coverVideoTriggeredAt || article.coverVideoGeneratedAt)
    ) {
      return;
    }

    const timeout = setTimeout(async () => {
      console.log('Re-fetching article data to pull missing information', {
        articleId: articleId,
        status: article.status,
        imageUrl: article.coverImage?.originalUrl,
      });
      const refreshedArticle = await fetchArticle(articleId);
      setArticle(refreshedArticle);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [article]);

  useEffect(() => {
    if (!user) return;
    if (!book?.isCollaborative()) return;
    if (!book?.isAmongBookAuthors(user?.id)) return;

    if (!getFeatureIntroducedToUserAt(Feature.BOOK_COLLABORATION)) {
      setCollaborationIntroOpen(true);
    }
  }, [book, user]);

  const [isQuizLoaded, setIsQuizLoaded] = useState<boolean | null>(false);
  const articleContentRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<UserArticleProgressState>();
  const stateRef = useRef(state);
  const [newlyRevealedSection, setNewlyRevealedSection] = useState<
    number | null
  >(null);
  const [sidebarContentType, setSidebarContentType] =
    useState<ReadArticleSidebarContentType | null>(null);
  const [quizStage, setQuizStage] = useState<UserArticleQuizStage | null>();
  const quizStageRef = useRef<UserArticleQuizStage | null>();
  quizStageRef.current = quizStage;

  const { isArticleRead, recordWordsRead, markArticleAsRead } =
    useArticleActivity(
      article,
      articleId,
      sidebarContentType === ReadArticleSidebarContentType.QUIZ
        ? quizStage
        : 'chapter_builder',
      props.isReadingSharedArticle || false,
      props.sharedArticleUtmMedium,
    );

  const { stopReadAloud } = useArticleReadAloud(sections, paragraphs);

  // Stop audio playback when navigating between routes
  useEffect(() => {
    stopReadAloud();
  }, [location]);

  useEffect(() => {
    setDoNotDisturb(true);

    return () => {
      setDoNotDisturb(false);
    };
  }, [quizStage]);

  const isQuizEnabled = useMemo(() => {
    return userType === UserType.Student;
  }, [userType]);

  const isLoading = useMemo(() => {
    if (!isArticleLoaded) {
      return true;
    }

    if (isQuizEnabled && !isQuizLoaded) {
      return true;
    }

    return false;
  }, [isArticleLoaded, isQuizEnabled, isQuizLoaded]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (newlyRevealedSection !== null) {
      timeoutId = setTimeout(() => {
        setNewlyRevealedSection(null);
      }, 4000);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [newlyRevealedSection]);

  useEffect(() => {
    if (isLoading) return;
    if (!sections?.length) return;
    if (isArticleRead) return;
    if (!articleContentRef.current) return;

    // Consider the article read if all sections are unblurred and the user has scrolled to the bottom (with some buffer)
    if (sections.some((section) => section.isBlurred)) {
      return;
    }

    if (
      windowScrollY + window.innerHeight >
      articleContentRef.current.scrollHeight - ARTICLE_READ_SCROLL_BUFFER
    ) {
      markArticleAsRead();
    }
  }, [windowScrollY, isArticleRead, isLoading, sections]);

  const handleQuizLoaded = (
    stage: UserArticleQuizStage | null,
    sections: Section[],
  ) => {
    setSections(sections);
    setIsQuizLoaded(true);
    setQuizStage(stage);
  };

  const handleContentTypeChange = (
    contentType: ReadArticleSidebarContentType | null,
  ) => {
    setSidebarContentType(contentType);
  };

  const handleNextQuizQuestion = (
    question: Question,
    nextQuestion: Question | null,
  ) => {
    if (question.relatedPassageStartIndex || question.relatedPassageEndIndex) {
      const relatedPassage = article.passage.substring(
        question.relatedPassageStartIndex,
        nextQuestion?.relatedPassageStartIndex,
      );
      const wordsCountInPassage = countWords(relatedPassage);
      recordWordsRead(wordsCountInPassage);
    }
    setState((prev) => {
      const newState = nextQuestion
        ? UserArticleProgressState.IN_PROGRESS
        : UserArticleProgressState.COMPLETED;

      if (
        question.questionType === QuestionType.GUIDING &&
        nextQuestion?.questionType === QuestionType.ASSESSMENT
      ) {
        setQuizStage(UserArticleQuizStage.ASSESSMENT);
      } else if (!nextQuestion) {
        console.debug('Setting stage to REPORT');
        setQuizStage(UserArticleQuizStage.REPORT);
      }

      if (newState !== prev) {
        console.debug(
          `Recording Progress Change: ${newState} -> ${prev}`,
          articleId,
        );
        const event = new ArticleProgressChangeEvent(articleId, newState);
        recordEvent(event);
      } else {
        console.debug('No Progress Change to record', articleId);
      }
      return (stateRef.current = newState);
    });

    const sectionToRevealIndex = sections.findIndex(
      (section) =>
        section.questionId === nextQuestion?.id || section.questionId === null,
    );

    if (sectionToRevealIndex >= 0 && sections[sectionToRevealIndex].isBlurred) {
      setNewlyRevealedSection(sectionToRevealIndex);
      setSections((prevSections: Section[]) => {
        return prevSections.map((section: Section, index: number) => {
          if (index === sectionToRevealIndex) {
            return {
              ...section,
              isBlurred: false,
            };
          }
          return section;
        });
      });
    }

    if (question.questionType === QuestionType.GUIDING) {
      setTimeout(() => {
        const section = sections.find(
          (s) => s.questionId === nextQuestion?.id || s.questionId === null,
        );
        if (section && articleContentRef.current) {
          const sectionElement = articleContentRef.current.querySelector(
            `[data-question-id="${section.questionId}"]`,
          );
          if (sectionElement) {
            sectionElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }, 100);
    }
  };

  const showCollaborationIntro = () => {
    setCollaborationIntroOpen(true);
  };

  const closeCollaborationIntro = () => {
    setCollaborationIntroOpen(false);
  };

  const continueToArticle = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      recordFeatureIntroducedToUserAt(Feature.BOOK_COLLABORATION, new Date());
    }
    setCollaborationIntroOpen(false);
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      {isLoading && (
        <div
          className="flex-1 flex justify-center items-center"
          style={{ height: 'calc(100dvh - 60px)' }}
        >
          <LoadingIcon />
        </div>
      )}

      <div className={`mt-8 max-sm:mt-3 ${isLoading ? 'hidden' : ''} `}>
        <div className="gap-5 flex w-full max-md:flex-col max-md:items-stretch max-md:gap-0">
          <div
            className="flex w-full justify-center items-stretch px-3
            max-sm:flex-col max-sm:bg-white max-sm:md:px-1
            max-md:w-full max-md:px-2
          "
          >
            {isArticleLoaded && article ? (
              <>
                <div
                  className="items-stretch flex-col flex-1 max-w-[1000px] bg-white
                sm:rounded-xl
                md:md:mr-3
                xl:mr-5"
                  ref={articleContentRef}
                  data-testid="read-article-view-section"
                >
                  <div className="">
                    {/* Collaborative tags */}
                    {book?.isCollaborative() && myCoAuthor && chapter && (
                      <div className="mb-2 flex justify-between gap-2 max-md:flex-col">
                        <div className="flex items-start gap-2">
                          {book.isAbandoned() ? (
                            <CoAuthorAbandoned coAuthor={myCoAuthor} />
                          ) : (
                            <CoAuthorTag coAuthor={myCoAuthor} />
                          )}
                          <button
                            onClick={showCollaborationIntro}
                            className="flex items-center gap-1 px-3 py-1  text-blue-700 text-sm"
                          >
                            <FontAwesomeIcon
                              icon={faQuestionCircle}
                              className="mr-1"
                            />
                            <span>How does it work?</span>
                          </button>
                        </div>
                        <CollaborationStatusTag />
                      </div>
                    )}

                    <ArticleCover article={article} />
                    <div className="flex flex-row justify-between pt-6">
                      <div className="flex flex-col items-start max-w-4/5">
                        {book && chapter && (
                          <div className="flex flex-row justify-between items-center gap-2">
                            <div className="text-sm text-gray-500 font-bold">
                              Chapter {chapter.chapterNumber}/
                              {book.numberOfChapters}
                            </div>
                          </div>
                        )}
                        <header
                          className="flex grow shrink basis-auto"
                          data-testid="article-title"
                        >
                          <span className="text-black text-3xl font-bold text-left">
                            {article.title}
                          </span>
                        </header>
                      </div>
                      <div className="hidden md:flex h-0">
                        <ArticleStats
                          wordCount={article.wordCount}
                          grade={article.grade}
                          lexileLevel={article.lexileLevel}
                          flexDirection="col"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex md:hidden">
                    <ArticleStats
                      wordCount={article.wordCount}
                      grade={article.grade}
                      lexileLevel={article.lexileLevel}
                      flexDirection="row"
                    />
                  </div>
                  <ArticleContent
                    article={article}
                    sections={sections}
                    paragraphs={paragraphs}
                    newlyRevealedSection={newlyRevealedSection}
                  />
                </div>

                {isQuizEnabled && isArticleLoaded && article ? (
                  <ReadArticleSidebar
                    article={article}
                    onLoad={handleQuizLoaded}
                    onNextQuestion={handleNextQuizQuestion}
                    onContentTypeChange={handleContentTypeChange}
                  />
                ) : null}
              </>
            ) : null}

            {user === null && <SignUpPrompt />}
          </div>
        </div>
      </div>

      {/* Collaboration Intro Modal */}
      <CollaborationIntroModal
        isOpen={isCollaborationIntroOpen}
        onClose={closeCollaborationIntro}
        onContinue={continueToArticle}
      />
    </>
  );
}

export default ReadArticleView;
